import React, {useEffect} from 'react';
import dayjs from 'dayjs';

import {Period, PeriodPicker} from '@deviceinsight/ng-ui-components';

import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import TimePeriodPicker from './TimePeriodPicker';
import TimeRangeInput from './TimeRangeInput';
import WeekdaysPicker from './WeekdaysPicker';
import {
	KpiPeriodTypeType,
	KpiTimeframeType,
	WorkingShiftDay,
	canAddShift,
	getDateRange,
	PERIOD_TIMEFRAME_DEFAULT,
	PERIOD_TYPE_DEFAULT
} from '../kpiUtils';

import './TimeConfig.scss';
import FormRow from '../../../../inputs/FormRow';
import ToggleSwitch from '../../../../inputs/ToggleSwitch';

type Props = {
	periodTimeframe: KpiTimeframeType;
	periodType: KpiPeriodTypeType | null | undefined;
	periodCount: number;
	customPeriodFrom: number | null | undefined;
	customPeriodTo: number | null | undefined;
	workingShiftEnabled: boolean;
	workingShiftTimeFrom: string | null | undefined;
	workingShiftTimeTo: string | null | undefined;
	workingShiftDays: WorkingShiftDay[] | null | undefined;
	onPeriodTimeframePeriodTypeChange: (
		periodTimeframe: KpiTimeframeType,
		periodType: KpiPeriodTypeType | null | undefined
	) => void;
	onPeriodCountChange: (periodCount: number) => void;
	onCustomPeriodChange: (
		customPeriodFrom: number | null | undefined,
		customPeriodTo: number | null | undefined
	) => void;
	onWorkingShiftEnabledChange: (workingShiftEnabled: boolean) => void;
	onWorkingShiftTimeChange: (
		workingShiftTimeFrom: string | null | undefined,
		workingShiftTimeTo: string | null | undefined
	) => void;
	onWorkingShiftDaysChange: (workingShiftDays: WorkingShiftDay[]) => void;
};
export default function TimeConfig({
	periodTimeframe,
	periodType,
	periodCount,
	customPeriodFrom,
	customPeriodTo,
	workingShiftEnabled,
	workingShiftTimeFrom,
	workingShiftTimeTo,
	workingShiftDays,
	onPeriodTimeframePeriodTypeChange,
	onPeriodCountChange,
	onCustomPeriodChange,
	onWorkingShiftEnabledChange,
	onWorkingShiftTimeChange,
	onWorkingShiftDaysChange
}: Props) {
	useEffect(() => {
		// Initialize periodTimeframe if not yet defined
		if (!periodTimeframe) {
			onPeriodTimeframePeriodTypeChange(PERIOD_TIMEFRAME_DEFAULT, PERIOD_TYPE_DEFAULT);
			return;
		}

		// Initialize workingShiftEnabled if not yet defined (== checks for undefined and null)
		if (workingShiftEnabled == null) {
			onWorkingShiftEnabledChange(!!workingShiftDays && workingShiftDays.length > 0);
			return;
		}

		// Initialize workingShiftDays if not yet defined
		if (workingShiftDays == null) {
			onWorkingShiftDaysChange([]);
			return;
		}
	}, [
		onPeriodTimeframePeriodTypeChange,
		onWorkingShiftDaysChange,
		onWorkingShiftEnabledChange,
		periodTimeframe,
		workingShiftDays,
		workingShiftEnabled
	]);

	if (!periodTimeframe || workingShiftEnabled == null || workingShiftDays == null) {
		return null;
	}

	function getPeriodFromDateRange() {
		const {start, end} = getDateRange(dayjs(), {
			periodTimeframe,
			periodType,
			periodCount: isNaN(periodCount) ? 1 : periodCount,
			customPeriodFrom,
			customPeriodTo,
			workingShiftEnabled,
			workingShiftTimeFrom,
			workingShiftTimeTo,
			workingShiftDays
		});
		return {
			tsStart: start.valueOf(),
			tsEnd: end.valueOf()
		};
	}

	function getCustomPeriod(): Period {
		return {
			tsStart: customPeriodFrom || dayjs().startOf('hour').subtract(1, 'days').valueOf(),
			tsEnd: customPeriodTo || dayjs().startOf('hour').valueOf()
		};
	}

	const period = periodTimeframe === 'CUSTOM' ? getCustomPeriod() : getPeriodFromDateRange();
	const readOnly = periodTimeframe !== 'CUSTOM';
	return (
		<div className="fields">
			<div className="field">
				<label>
					<Trans i18nKey="dashboard.widgets.kpi.dateRange">Date range</Trans>
				</label>
				<div className="kpi-widget-time-config">
					<FormRow>
						<TimePeriodPicker
							periodTimeframe={periodTimeframe}
							periodType={periodType}
							onChange={onPeriodTimeframePeriodTypeChange}
						/>

						{(periodTimeframe === 'LAST' || periodTimeframe === 'PAST') && (
							<input
								type="number"
								min={1}
								max={999}
								value={periodCount || ''}
								onChange={(event) => onPeriodCountChange(parseInt(event.target.value))}
								required
								style={{
									width: 100
								}}
							/>
						)}
					</FormRow>

					<FormRow>
						<PeriodPicker
							period={period}
							onChange={(period: Period) => onCustomPeriodChange(period.tsStart, period.tsEnd)}
							readOnly={readOnly}
						/>
					</FormRow>

					{canAddShift(periodTimeframe, periodType) && (
						<>
							<FormRow>
								<ToggleSwitch
									label={<Trans i18nKey="dashboard.widgets.kpi.workingShift">Working Shift</Trans>}
									value={workingShiftEnabled}
									onChange={onWorkingShiftEnabledChange}
								/>
							</FormRow>
							{workingShiftEnabled && (
								<FormRow>
									<WeekdaysPicker
										days={periodType !== 'DAY' ? workingShiftDays : []}
										onDaysChange={onWorkingShiftDaysChange}
										periodType={periodType}
									/>
									<TimeRangeInput
										timeFrom={workingShiftTimeFrom}
										timeTo={workingShiftTimeTo}
										onChangeTime={onWorkingShiftTimeChange}
										periodType={periodType}
										disabled={false}
									/>
								</FormRow>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
